import { useState, useEffect } from 'react';
import { ArrowDownUp, RefreshCw, History, Clock, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import SwapWidget from '../SwapWidget';
import { tradingAPI } from '../../services/api';
import { toast } from 'sonner';

import { fmtFixed } from '../../utils/format';
const CryptoConversionView = ({ initialTab = 'swap' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [swapHistory, setSwapHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Fetch swap history
  const fetchSwapHistory = async () => {
    setLoadingHistory(true);
    try {
      const history = await tradingAPI.getSwapHistory(50);
      setSwapHistory(history || []);
    } catch (error) {
      console.error('Error fetching swap history:', error);
      // Don't show error toast, just set empty array
      setSwapHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      fetchSwapHistory();
    }
  }, [activeTab]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  // Format number
  const formatNumber = (num, decimals = 8) => {
    if (!num && num !== 0) return '0';
    return fmtFixed(parseFloat(num), decimals);
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-2 bg-gray-900/50 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('swap')}
          className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-all ${
            activeTab === 'swap'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
          }`}
        >
          <ArrowDownUp className="h-4 w-4" />
          <span>Swap</span>
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-all ${
            activeTab === 'history'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
          }`}
        >
          <History className="h-4 w-4" />
          <span>History</span>
        </button>
      </div>

      {/* Swap Tab */}
      {activeTab === 'swap' && <SwapWidget />}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="glow-card p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Swap History</h2>
            <button
              onClick={fetchSwapHistory}
              disabled={loadingHistory}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <RefreshCw className={`h-5 w-5 ${loadingHistory ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {loadingHistory ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="h-10 w-10 animate-spin text-purple-500 mx-auto mb-4" />
                <p className="text-gray-400">Loading swap history...</p>
              </div>
            </div>
          ) : swapHistory.length === 0 ? (
            <div className="text-center py-20">
              <ArrowDownUp className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">No Swap History</h3>
              <p className="text-gray-500">Your completed swaps will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {swapHistory.map((swap, index) => (
                <div
                  key={swap.id || index}
                  className="bg-gray-900/50 rounded-xl p-4 border border-gray-800 hover:border-purple-500/30 transition-colors"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-400">
                        {formatDate(swap.swap_time || swap.recorded_at)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {swap.status === 'completed' ? (
                        <span className="flex items-center text-green-400 text-sm">
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Completed
                        </span>
                      ) : (
                        <span className="flex items-center text-red-400 text-sm">
                          <XCircle className="h-4 w-4 mr-1" />
                          {swap.status || 'Unknown'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Swap Details */}
                  <div className="flex items-center justify-between">
                    {/* From */}
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">From</p>
                      <p className="text-lg font-semibold text-white font-mono">
                        {formatNumber(swap.from_amount, 6)} <span className="text-purple-400">{swap.from_crypto_symbol}</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        ≈ ${formatNumber(swap.from_value_usdt, 2)}
                      </p>
                    </div>

                    {/* Arrow */}
                    <div className="px-4">
                      <div className="bg-purple-600/20 p-2 rounded-full">
                        <ArrowRight className="h-5 w-5 text-purple-400" />
                      </div>
                    </div>

                    {/* To */}
                    <div className="flex-1 text-right">
                      <p className="text-xs text-gray-500 mb-1">To</p>
                      <p className="text-lg font-semibold text-white font-mono">
                        {formatNumber(swap.to_amount, 6)} <span className="text-green-400">{swap.to_crypto_symbol}</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        ≈ ${formatNumber(swap.to_value_usdt, 2)}
                      </p>
                    </div>
                  </div>

                  {/* Exchange Rate & Fee */}
                  <div className="mt-3 pt-3 border-t border-gray-800 flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      Rate: 1 {swap.from_crypto_symbol} = {formatNumber(swap.exchange_rate, 6)} {swap.to_crypto_symbol}
                    </span>
                    <span className="text-gray-500">
                      Fee: ${formatNumber(swap.fee_amount_usdt, 4)} (0.1%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CryptoConversionView;
